import CountryTag from '@/app/[lng]/components/CountryTag'
import {
  gtmcompanyCardViewTracker,
  gtmFromRedirectedToCompany,
} from '@/app/[lng]/components/GTMTrackers/GTMCompanyTracker'
import PlanLogo from '@/app/[lng]/components/PlanLogo'
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver'
import { useTranslation } from '@/app/i18n/client'
import { Activity, Company } from '@/app/types/companySearchType'
import { companyProfileRedirection } from '@/lib/redirectionUtils'
import { Tooltip } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import React, { MutableRefObject, useEffect, useRef } from 'react'

const SearchListingCompanyCard = ({
  company,
  showLogo,
}: {
  company: Company
  showLogo: boolean
}) => {
  const { t } = useTranslation()
  const params = useParams()
  const companyCardRef: MutableRefObject<HTMLAnchorElement | null> =
    useRef(null)
  const handleIntersection = (element: Element) => {
    const id = element.id
    gtmcompanyCardViewTracker(Number(id))
  }

  const observeElement = useIntersectionObserver(handleIntersection)

  useEffect(() => {
    if (companyCardRef.current) {
      observeElement(companyCardRef.current)
    }
  }, [observeElement])

  return (
    <Link
      ref={companyCardRef}
      id={String(company?.id)}
      href={companyProfileRedirection(
        company?.industry?.name,
        company?.friendly_url,
        String(params?.lng) ?? 'en'
      )}
      onClick={() => {
        gtmFromRedirectedToCompany(
          'company_listing_card',
          company?.friendly_url,
          company?.id?.toString()
        )
      }}
    >
      <div className="flex flex-row w-[100%] bg-[#fdfdfd] rounded-[5px] border-[1px] border-solid border-[#ededed] p-3 cursor-pointer hover:border-[#d4d4d4]  mobile-width">
        {showLogo && (
          <div className="w-[15%] mobile:w-[100px] mobile:h-[100px]">
            <Image
              loading="lazy"
              width={71}
              height={68}
              className=" h-[68px] object-contain rounded"
              alt="company-img"
              src={company?.logo ?? '/assets/Icons/noImage/noImage2.svg'}
            />
          </div>
        )}
        <div className="w-[85%] flex flex-col text-left gap-2 mobile:gap-1">
          <div className="mb-3">
            <div className="flex gap-4 items-center mb-1 mobile:flex-col mobile:items-start mobile:gap-1 ">
              <span className="text-deep_black text-lg font-ns_bold text-ellipsis capitalize">
                {company?.name}
              </span>
              {company?.plan !== 'Free' && (
                <PlanLogo planType={company?.plan} />
              )}
            </div>
            <div className="h-3">
              <CountryTag name={company?.country} size="xs" />
            </div>
          </div>
          {company?.address?.country?.name ? (
            <div className=" flex items-center text-xs mb-1">
              <CountryTag
                name={
                  company?.address?.country?.name
                    ? company?.address?.country?.name
                    : t('country not available')
                }
              />
            </div>
          ) : (
            ''
          )}
          <div className="text-sm">
            <span className="text-deep_black font-ns_bold">
              {t('Activities')}:
            </span>
            <span className="ml-1 text-primary-foreground not-italic">
              {company?.activities?.length > 0
                ? company?.activities?.map(
                    (activity: Activity, index: number) => (
                      <React.Fragment key={`${index}-activity` + Math.random()}>
                        {activity.name}
                        {index !== company?.activities.length - 1 && (
                          <span className="me-1">, </span>
                        )}
                      </React.Fragment>
                    )
                  )
                : t('No Activity Found')}
            </span>
          </div>
          {company?.industry?.name !== 'AF Retail Chains' && (
            <div className="text-sm flex">
              <span className="text-deep_black font-ns_bold">
                {t('Products')}:
              </span>
              <span className="ml-1 text-primary-foreground flex flex-wrap">
                {company?.products?.company_products?.length === 0
                  ? t('No Products Available')
                  : company?.products?.company_products?.map(
                      (item: any, index: number) =>
                        index < 10 ? (
                          <span key={index}>
                            {item.product.name}
                            <span className="mr-1">
                              {index <
                                company.products.company_products.length - 1 &&
                                ', '}
                            </span>
                          </span>
                        ) : index === 10 ? (
                          <Tooltip
                            key={index}
                            title={company.products.company_products
                              .slice(10)
                              .map((item: any) => item.product.name)
                              .join(', ')}
                            arrow
                          >
                            <p className="relative cursor-pointer ml-1">{`+${company.products.company_products.length - 10}`}</p>
                          </Tooltip>
                        ) : null
                    )}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

export default SearchListingCompanyCard
